import React, { useState } from "react";
import {
  Plus,
  GripVertical,
  Check,
  X,
  Pencil,
  Trash2,
  ReceiptEuro,
  FileText,
  BadgeCheck,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useCase } from "../../context/CaseContext";
import { CHARGEBEE_WEBHOOK_URL } from "../../config/constants";

const PurchasedServicesPanel: React.FC = () => {
  const {
    purchasedProducts,
    setPurchasedProducts,
    taxEnabled,
    setTaxEnabled,
    taxRate,
    subtotalOneOff,
    subtotalAnnual,
    totalGross,
    savePartial,
    logAuditAction,
    currentCaseId,
    clientName,
    user,
    chargebeeCustomerId,
    hubspotIds,
    contactEmail,
    billingDetails,
    webDevDetails,
  } = useCase();

  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    description: "",
    quantity: "1",
    price: "",
    billingFrequency: "one-time",
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingProduct, setEditingProduct] = useState<any>({});
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);

  const parsePrice = (price: any) =>
    parseFloat(String(price).replace(/[^0-9.-]+/g, "")) || 0;

  const handleDrag = (
    list: any[],
    setList: any,
    startIdx: number,
    enterIdx: number,
  ) => {
    const u = [...list];
    const item = u[startIdx];
    u.splice(startIdx, 1);
    u.splice(enterIdx, 0, item);
    setList(u);
  };

  const updateItem = (
    list: any[],
    setList: any,
    idx: number,
    item: any,
    saveKey: string,
  ) => {
    const u = [...list];
    u[idx] = item;
    setList(u);
    savePartial({ [saveKey]: u });
  };

  const handleSendInvoice = async () => {
    const itemsToInvoice = purchasedProducts
      .map((p, i) => ({ ...p, originalIndex: i }))
      .filter((p) => !p.invoiced);

    if (itemsToInvoice.length === 0) {
      toast("All items are already invoiced.", { icon: "✅" });
      return;
    }

    if (
      !window.confirm(
        `Send invoice for ${itemsToInvoice.length} items to Chargebee?`,
      )
    ) {
      return;
    }

    setIsSendingInvoice(true);

    const emails = Array.from(
      new Set(
        [contactEmail, billingDetails.email, webDevDetails.email]
          .map((e) => e?.trim())
          .filter((e) => e && e.includes("@")),
      ),
    );

    const payload = {
      caseId: currentCaseId,
      customerId: chargebeeCustomerId,
      hubspotIds: hubspotIds,
      emails,
      items: itemsToInvoice.map((p) => ({
        name: p.name,
        quantity: parseFloat(p.quantity) || 1,
        price: parsePrice(p.price),
      })),
      timestamp: new Date().toISOString(),
    };

    try {
      const response = await fetch(CHARGEBEE_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success("Invoice sent to Chargebee!");

        // Mark items as invoiced
        const newProducts = [...purchasedProducts];
        itemsToInvoice.forEach((item) => {
          if (newProducts[item.originalIndex]) {
            newProducts[item.originalIndex] = {
              ...newProducts[item.originalIndex],
              invoiced: true,
            };
          }
        });
        setPurchasedProducts(newProducts);
        savePartial({ purchasedProducts: newProducts });

        logAuditAction(
          "INVOICE_SENT",
          {
            collection: "onboarding_cases",
            id: currentCaseId || "",
            name: clientName || "Unknown",
          },
          { uid: user?.uid || "unknown", email: user?.email || "unknown" },
          [
            {
              field: "invoicedItems",
              oldValue: null,
              newValue: itemsToInvoice.map((i) => i.name).join(", "),
            },
          ],
        );
      } else {
        const text = await response.text();
        console.error("Invoice Webhook Error:", text);
        toast.error("Failed to send invoice. Check webhook.");
      }
    } catch (error) {
      console.error("Invoice Error:", error);
      toast.error("Network error sending invoice.");
    } finally {
      setIsSendingInvoice(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
      <div className="bg-slate-50 dark:bg-slate-900/50 px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <ReceiptEuro size={16} /> Purchased Services
        </h3>
        <div className="flex gap-2">
          <button
            onClick={handleSendInvoice}
            disabled={isSendingInvoice}
            className="text-xs bg-indigo-50 border border-indigo-200 text-indigo-600 px-3 py-1.5 rounded-lg font-bold hover:bg-indigo-100 transition-all flex items-center gap-1 shadow-sm disabled:opacity-50"
          >
            <FileText size={14} />{" "}
            {isSendingInvoice ? "Sending..." : "Send Invoice"}
          </button>
          <button
            onClick={() => setIsAddingProduct(true)}
            className="text-xs bg-white border border-gray-300 text-gray-600 px-3 py-1.5 rounded-lg font-bold hover:bg-gray-50 transition-all flex items-center gap-1 shadow-sm"
          >
            <Plus size={14} /> Add Item
          </button>
        </div>
      </div>

      {isAddingProduct && (
        <div className="p-4 bg-blue-50 dark:bg-slate-900 border-b dark:border-slate-700 animate-in slide-in-from-top-2">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-6">
              <label className="text-[10px] uppercase font-bold text-gray-500">
                Name
              </label>
              <input
                className="w-full border p-2 rounded text-sm dark:bg-slate-800 dark:text-white"
                value={newProduct.name}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, name: e.target.value })
                }
                autoFocus
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase font-bold text-gray-500">
                Qty
              </label>
              <input
                type="number"
                className="w-full border p-2 rounded text-sm dark:bg-slate-800 dark:text-white"
                value={newProduct.quantity}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, quantity: e.target.value })
                }
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase font-bold text-gray-500">
                Price
              </label>
              <input
                type="number"
                className="w-full border p-2 rounded text-sm dark:bg-slate-800 dark:text-white"
                value={newProduct.price}
                onChange={(e) =>
                  setNewProduct({ ...newProduct, price: e.target.value })
                }
              />
            </div>
            <div className="md:col-span-2">
              <button
                onClick={() => {
                  if (!newProduct.name) return;
                  setPurchasedProducts([...purchasedProducts, newProduct]);
                  savePartial({
                    purchasedProducts: [...purchasedProducts, newProduct],
                  });

                  // Audit Log
                  logAuditAction(
                    "UPDATE",
                    {
                      collection: "onboarding_cases",
                      id: currentCaseId || "",
                      name: clientName || "Unknown Case",
                    },
                    {
                      uid: user?.uid || "unknown",
                      email: user?.email || "unknown",
                    },
                    [
                      {
                        field: "purchasedProducts",
                        oldValue: null,
                        newValue: `Added: ${newProduct.name} (x${newProduct.quantity})`,
                      },
                    ],
                  );

                  setNewProduct({
                    name: "",
                    description: "",
                    quantity: "1",
                    price: "",
                    billingFrequency: "one-time",
                  });
                  setIsAddingProduct(false);
                }}
                className="w-full bg-blue-600 text-white p-2 rounded h-[38px] shadow-sm font-bold"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left dark:text-slate-300">
          <thead className="hidden md:table-header-group text-xs text-gray-500 uppercase bg-white dark:bg-slate-800 border-b dark:border-slate-700">
            <tr>
              <th className="px-6 py-3 w-10"></th>
              <th className="px-6 py-3">Description</th>
              <th className="px-6 py-3 text-right">Qty</th>
              <th className="px-6 py-3 text-right">Price</th>
              <th className="px-6 py-3 text-right">Total</th>
              <th className="px-4 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {purchasedProducts.map((p, idx) => {
              const isEditing = editingIndex === idx;
              return (
                <tr
                  key={idx}
                  draggable={!isEditing}
                  onDragStart={() => setDraggedItemIndex(idx)}
                  onDragEnter={() =>
                    handleDrag(
                      purchasedProducts,
                      setPurchasedProducts,
                      draggedItemIndex!,
                      idx,
                    )
                  }
                  onDragEnd={() => {
                    setDraggedItemIndex(null);
                    savePartial({ purchasedProducts });
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  className={`flex flex-col md:table-row bg-white dark:bg-slate-800 p-4 md:p-0 border-b md:border-b-0 border-gray-100 group hover:bg-slate-50 dark:hover:bg-slate-700/50 ${draggedItemIndex === idx ? "opacity-50 bg-blue-50" : ""} ${p.invoiced ? "bg-green-50/30 dark:bg-green-900/10" : ""}`}
                >
                  <td className="hidden md:table-cell px-6 py-4 text-gray-300 cursor-grab hover:text-gray-500">
                    <GripVertical size={16} />
                  </td>
                  <td className="md:px-6 md:py-4 mb-2 md:mb-0 w-full md:w-[40%]">
                    {isEditing ? (
                      <div className="space-y-2">
                        <input
                          className="border rounded p-1.5 w-full dark:bg-slate-900 dark:text-white font-bold"
                          value={editingProduct.name}
                          onChange={(e) =>
                            setEditingProduct({
                              ...editingProduct,
                              name: e.target.value,
                            })
                          }
                        />
                        <textarea
                          className="border rounded p-1.5 w-full dark:bg-slate-900 dark:text-white text-xs resize-y"
                          rows={4}
                          value={editingProduct.description || ""}
                          onChange={(e) =>
                            setEditingProduct({
                              ...editingProduct,
                              description: e.target.value,
                            })
                          }
                          placeholder="Description..."
                        />
                        <select
                          className="border rounded p-1 w-full text-xs dark:bg-slate-900 dark:text-white"
                          value={editingProduct.billingFrequency}
                          onChange={(e) =>
                            setEditingProduct({
                              ...editingProduct,
                              billingFrequency: e.target.value,
                            })
                          }
                        >
                          <option value="one-time">One-Time</option>
                          <option value="annually">Annually</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                    ) : (
                      <div>
                        <div className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                          {p.name}
                          {p.billingFrequency && (
                            <span className="text-[10px] bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded text-gray-500 uppercase">
                              {p.billingFrequency}
                            </span>
                          )}
                        </div>
                        {p.description && (
                          <div className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">
                            {p.description}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="md:px-6 md:py-4 text-right flex justify-between md:table-cell">
                    <span className="md:hidden text-gray-400 text-xs font-bold">
                      Qty
                    </span>
                    {isEditing ? (
                      <input
                        className="border rounded p-1 w-16 text-right dark:bg-slate-900"
                        value={editingProduct.quantity}
                        onChange={(e) =>
                          setEditingProduct({
                            ...editingProduct,
                            quantity: e.target.value,
                          })
                        }
                      />
                    ) : (
                      p.quantity
                    )}
                  </td>
                  <td className="md:px-6 md:py-4 text-right flex justify-between md:table-cell">
                    <span className="md:hidden text-gray-400 text-xs font-bold">
                      Price
                    </span>
                    {isEditing ? (
                      <input
                        className="border rounded p-1 w-20 text-right dark:bg-slate-900"
                        value={editingProduct.price}
                        onChange={(e) =>
                          setEditingProduct({
                            ...editingProduct,
                            price: e.target.value,
                          })
                        }
                      />
                    ) : (
                      `€${parsePrice(p.price).toFixed(2)}`
                    )}
                  </td>
                  <td className="md:px-6 md:py-4 text-right font-bold flex justify-between md:table-cell">
                    <span className="md:hidden text-gray-400 text-xs font-bold">
                      Total
                    </span>
                    €
                    {(
                      parsePrice(isEditing ? editingProduct.price : p.price) *
                      (parseFloat(
                        isEditing ? editingProduct.quantity : p.quantity,
                      ) || 1)
                    ).toFixed(2)}
                  </td>
                  <td className="md:px-4 md:py-4 text-right flex justify-end md:table-cell pt-2 md:pt-4">
                    {isEditing ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            updateItem(
                              purchasedProducts,
                              setPurchasedProducts,
                              editingIndex!,
                              editingProduct,
                              "purchasedProducts",
                            );
                            setEditingIndex(null);
                          }}
                          className="text-green-600"
                        >
                          <Check size={18} />
                        </button>
                        <button
                          onClick={() => setEditingIndex(null)}
                          className="text-red-500"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-end gap-2">
                        {p.invoiced && (
                          <button
                            onClick={() => {
                              if (window.confirm("Unmark item as invoiced?")) {
                                const u = [...purchasedProducts];
                                u[idx] = { ...u[idx], invoiced: false };
                                setPurchasedProducts(u);
                                savePartial({ purchasedProducts: u });
                              }
                            }}
                            className="flex items-center gap-1 text-[10px] bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 px-2 py-0.5 rounded uppercase font-bold hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/50 dark:hover:text-red-400 transition-colors"
                            title="Click to Un-invoice"
                          >
                            <BadgeCheck size={12} /> Invoiced
                          </button>
                        )}
                        <div className="flex justify-end gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setEditingProduct({ ...p });
                              setEditingIndex(idx);
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600 dark:text-slate-500 dark:hover:text-blue-400 transition-colors"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm("Remove item?")) {
                                const u = [...purchasedProducts];
                                const removedItem = u[idx];
                                u.splice(idx, 1);
                                setPurchasedProducts(u);
                                savePartial({ purchasedProducts: u });

                                // Audit Log
                                logAuditAction(
                                  "UPDATE",
                                  {
                                    collection: "onboarding_cases",
                                    id: currentCaseId || "",
                                    name: clientName || "Unknown Case",
                                  },
                                  {
                                    uid: user?.uid || "unknown",
                                    email: user?.email || "unknown",
                                  },
                                  [
                                    {
                                      field: "purchasedProducts",
                                      oldValue: `Removed: ${removedItem.name}`,
                                      newValue: null,
                                    },
                                  ],
                                );
                              }
                            }}
                            className="p-1 text-gray-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-slate-50 dark:bg-slate-900 p-4 border-t border-gray-200 dark:border-slate-700 flex flex-col items-end">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-400">VAT ({taxRate}%)</span>
          <button
            onClick={() => setTaxEnabled(!taxEnabled)}
            className={`w-6 h-3 rounded-full p-0.5 transition-colors ${taxEnabled ? "bg-blue-600" : "bg-gray-300"}`}
          >
            <div
              className={`w-2 h-2 bg-white rounded-full transition-transform ${taxEnabled ? "translate-x-3" : ""}`}
            />
          </button>
        </div>
        <div className="text-sm text-slate-500 mb-1">
          One-off: €{subtotalOneOff.toFixed(2)} | Annual: €
          {subtotalAnnual.toFixed(2)}
        </div>
        <div className="text-lg font-bold text-slate-800 dark:text-white">
          Total: €{totalGross.toFixed(2)}
        </div>
      </div>
    </div>
  );
};

export default PurchasedServicesPanel;
